import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useLanguage } from '../contexts/LanguageContext';

function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('spielverlauf'); // 'spielverlauf', 'tabelle', 'teams'
  const [wsConnected, setWsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    const fetchTournamentData = async () => {
      try {
        const [tournamentRes, matchesRes, teamsRes] = await Promise.all([
          axios.get(`/api/tournaments/${id}`),
          axios.get(`/api/tournaments/${id}/matches`),
          axios.get(`/api/tournaments/${id}/teams`)
        ]);

        setTournament(tournamentRes.data);
        setMatches(matchesRes.data);
        setTeams(teamsRes.data);
      } catch (err) {
        console.error('Fehler beim Laden der Turnierdaten:', err);
        if (err.response?.status === 404) {
          setError(t('tournamentDetail.notFound'));
        } else {
          setError(t('tournamentDetail.loadError'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [id]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    socketRef.current = io(apiUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected (TournamentDetail)');
      setWsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      console.log('WebSocket disconnected (TournamentDetail)');
      setWsConnected(false);
    });

    socketRef.current.on('match_updated', (data) => {
      console.log('Match updated via WebSocket (TournamentDetail):', data);
      // Update local matches state with new match data
      setMatches((prevMatches) =>
        prevMatches.map((match) =>
          match.id === data.match_id
            ? { ...match, ...data }
            : match
        )
      );
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Join/leave tournament room when tournament ID changes
  useEffect(() => {
    if (!id || !socketRef.current) return;

    socketRef.current.emit('join_tournament', id);
    console.log(`Joined tournament room (TournamentDetail): ${id}`);

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_tournament', id);
        console.log(`Left tournament room (TournamentDetail): ${id}`);
      }
    };
  }, [id]);

  // Format date to German format (DD.MM.YYYY)
  const formatGermanDate = (dateString) => {
    if (!dateString) return '-';
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString; // Return original if not in expected format
    const [year, month, day] = parts;
    return `${day}.${month}.${year}`;
  };

  // Helper function to get team placeholder
  const getTeamPlaceholder = (match, teamPosition) => {
    const rounds = {
      'Viertelfinale': [],
      'Halbfinale': [],
      'Finale': [],
      'Spiel um Platz 3': []
    };

    matches.forEach(m => {
      if (rounds[m.round]) {
        rounds[m.round].push(m);
      }
    });

    const winnerFeedingMatches = matches.filter(m =>
      (teamPosition === 'team1' && m.winner_advances_to_match_id === match.id && m.winner_advances_to_position === 'team1') ||
      (teamPosition === 'team2' && m.winner_advances_to_match_id === match.id && m.winner_advances_to_position === 'team2')
    );

    const loserFeedingMatches = matches.filter(m =>
      (teamPosition === 'team1' && m.loser_advances_to_match_id === match.id && m.loser_advances_to_position === 'team1') ||
      (teamPosition === 'team2' && m.loser_advances_to_match_id === match.id && m.loser_advances_to_position === 'team2')
    );

    if (match.round === 'Halbfinale') {
      const qfMatches = winnerFeedingMatches.filter(m => m.round === 'Viertelfinale')
        .sort((a, b) => a.match_number - b.match_number);

      if (qfMatches.length >= 2) {
        if (teamPosition === 'team1') {
          return `${t('round.winnerOf')} ${t('round.quarterfinal')} ${qfMatches[0].match_number}`;
        } else {
          return `${t('round.winnerOf')} ${t('round.quarterfinal')} ${qfMatches[1].match_number}`;
        }
      } else if (qfMatches.length === 1) {
        return `${t('round.winnerOf')} ${t('round.quarterfinal')} ${qfMatches[0].match_number}`;
      }
    } else if (match.round === 'Finale') {
      const sfMatches = winnerFeedingMatches.filter(m => m.round === 'Halbfinale')
        .sort((a, b) => a.match_number - b.match_number);

      if (sfMatches.length >= 2) {
        if (teamPosition === 'team1') {
          return `${t('round.winnerOf')} ${t('round.semifinal')} ${sfMatches[0].match_number}`;
        } else {
          return `${t('round.winnerOf')} ${t('round.semifinal')} ${sfMatches[1].match_number}`;
        }
      } else if (sfMatches.length === 1) {
        return `${t('round.winnerOf')} ${t('round.semifinal')} ${sfMatches[0].match_number}`;
      }
    } else if (match.round === 'Spiel um Platz 3') {
      const sfMatches = loserFeedingMatches.filter(m => m.round === 'Halbfinale')
        .sort((a, b) => a.match_number - b.match_number);

      if (sfMatches.length >= 2) {
        if (teamPosition === 'team1') {
          return `${t('round.loserOf')} ${t('round.semifinal')} ${sfMatches[0].match_number}`;
        } else {
          return `${t('round.loserOf')} ${t('round.semifinal')} ${sfMatches[1].match_number}`;
        }
      } else if (sfMatches.length === 1) {
        return `${t('round.loserOf')} ${t('round.semifinal')} ${sfMatches[0].match_number}`;
      }
    }

    return 'TBD';
  };

  const translateRound = (roundName) => {
    const map = {
      'Finale': t('round.final'),
      'Halbfinale': t('round.semifinal'),
      'Viertelfinale': t('round.quarterfinal'),
      'Spiel um Platz 3': t('round.thirdPlace'),
    };
    return map[roundName] || roundName;
  };

  // Hilfsfunktion für Mexicano/Americano: Zeige nur Spielernamen, nicht Teamnamen
  const getDisplayName = (team) => {
    if (!team) return 'TBD';

    // Bei Mexicano/Americano: Jedes "Team" ist eigentlich ein einzelner Spieler
    if (tournament?.tournament_type === 'mexicano' || tournament?.tournament_type === 'americano') {
      // Zeige nur den ersten Spielernamen (bei Mexicano/Americano hat jedes Team genau einen Spieler)
      return team.players?.[0]?.name || team.name;
    }

    // Bei anderen Turnieren: Zeige alle Spieler mit " / " getrennt
    return team.players?.map(p => p.name).join(' / ') || team.name;
  };

  // Render match card (read-only version of admin card)
  const renderMatchCard = (match) => {
    const team1 = teams.find(t => t.id === match.team1_id);
    const team2 = teams.find(t => t.id === match.team2_id);
    const team3 = teams.find(t => t.id === match.team3_id);
    const team4 = teams.find(t => t.id === match.team4_id);

    // Für Best-of-3: Zähle gewonnene Sätze
    let score1 = match.team1_score;
    let score2 = match.team2_score;

    // Wenn Best-of-3 (kein team1_score aber team1_set1 vorhanden)
    if (score1 === null && match.team1_set1 !== null) {
      score1 = 0;
      score2 = 0;

      // Satz 1
      if (match.team1_set1 !== null && match.team2_set1 !== null) {
        if (match.team1_set1 > match.team2_set1) score1++;
        else if (match.team2_set1 > match.team1_set1) score2++;
      }

      // Satz 2
      if (match.team1_set2 !== null && match.team2_set2 !== null) {
        if (match.team1_set2 > match.team2_set2) score1++;
        else if (match.team2_set2 > match.team1_set2) score2++;
      }

      // Satz 3
      if (match.team1_set3 !== null && match.team2_set3 !== null) {
        if (match.team1_set3 > match.team2_set3) score1++;
        else if (match.team2_set3 > match.team1_set3) score2++;
      }

      // Wenn keine Sätze gespielt wurden, setze auf null
      if (match.team1_set1 === null) {
        score1 = null;
        score2 = null;
      }
    }

    // Mexicano Format
    const isMexicano = team3 && team4;

    if (isMexicano) {
      return (
        <div key={match.id} className="card" style={{
          padding: '12px',
          marginBottom: '10px',
          border: score1 !== null && score2 !== null ? '2px solid #22c55e' : '1px solid rgba(206, 212, 218, 0.5)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)'
        }}>
          {(match.court || match.start_time) && (
            <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: '6px', color: '#6c757d' }}>
              {match.court && <span>🏟️ {match.court}</span>}
              {match.court && match.start_time && <span> · </span>}
              {match.start_time && <span>⏰ {match.start_time}</span>}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '4px' }}>
            <span style={{ flex: 1, fontWeight: 'bold', color: '#0F172A', fontSize: '0.8rem', minWidth: 0, wordBreak: 'break-word', lineHeight: '1.2' }}>
              {getDisplayName(team1)} & {getDisplayName(team3)}
            </span>
            <div style={{
              width: '40px',
              padding: '4px',
              fontSize: '14px',
              textAlign: 'center',
              background: 'rgba(245, 242, 239, 0.9)',
              border: '1px solid rgba(206, 212, 218, 0.6)',
              color: '#0F172A',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              {score1 !== null ? score1 : '-'}
            </div>
            <span style={{ margin: '0 3px', color: '#6c757d', fontWeight: 'bold', flexShrink: 0 }}>:</span>
            <div style={{
              width: '40px',
              padding: '4px',
              fontSize: '14px',
              textAlign: 'center',
              background: 'rgba(245, 242, 239, 0.9)',
              border: '1px solid rgba(206, 212, 218, 0.6)',
              color: '#0F172A',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              {score2 !== null ? score2 : '-'}
            </div>
            <span style={{ flex: 1, fontWeight: 'bold', color: '#0F172A', textAlign: 'right', fontSize: '0.8rem', minWidth: 0, wordBreak: 'break-word', lineHeight: '1.2' }}>
              {getDisplayName(team2)} & {getDisplayName(team4)}
            </span>
          </div>

          {score1 !== null && score2 !== null && (
            <div style={{
              marginTop: '6px',
              textAlign: 'center',
              color: '#22c55e',
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }}>
              {t('tournamentDetail.finished')}
            </div>
          )}
        </div>
      );
    }

    // Standard Format
    return (
      <div key={match.id} className="card" style={{
        padding: '12px',
        marginBottom: '10px',
        border: score1 !== null && score2 !== null ? '2px solid #22c55e' : '1px solid rgba(206, 212, 218, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)'
      }}>
        {match.court && (
          <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: '6px', color: '#6c757d' }}>
            🏟️ {match.court}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '4px' }}>
          <span style={{ flex: 1, fontWeight: 'bold', color: '#0F172A', fontSize: '0.8rem', minWidth: 0, wordBreak: 'break-word', lineHeight: '1.2' }}>
            {team1 ? team1.players?.map(p => p.name).join(' / ') : getTeamPlaceholder(match, 'team1')}
          </span>
          <div style={{
            width: '40px',
            padding: '4px',
            fontSize: '14px',
            textAlign: 'center',
            background: 'rgba(245, 242, 239, 0.9)',
            border: '1px solid rgba(206, 212, 218, 0.6)',
            color: '#0F172A',
            fontWeight: 'bold',
            flexShrink: 0
          }}>
            {score1 !== null ? score1 : '-'}
          </div>
          <span style={{ margin: '0 3px', color: '#6c757d', fontWeight: 'bold', flexShrink: 0 }}>:</span>
          <div style={{
            width: '40px',
            padding: '4px',
            fontSize: '14px',
            textAlign: 'center',
            background: 'rgba(245, 242, 239, 0.9)',
            border: '1px solid rgba(206, 212, 218, 0.6)',
            color: '#0F172A',
            fontWeight: 'bold',
            flexShrink: 0
          }}>
            {score2 !== null ? score2 : '-'}
          </div>
          <span style={{ flex: 1, fontWeight: 'bold', color: '#0F172A', textAlign: 'right', fontSize: '0.8rem', minWidth: 0, wordBreak: 'break-word', lineHeight: '1.2' }}>
            {team2 ? team2.players?.map(p => p.name).join(' / ') : getTeamPlaceholder(match, 'team2')}
          </span>
        </div>

        {score1 !== null && score2 !== null && (
          <>
            {/* Zeige Satz-Details für Best-of-3 */}
            {match.team1_set1 !== null && (
              <div style={{
                marginTop: '8px',
                padding: '6px',
                backgroundColor: 'rgba(245, 242, 239, 0.5)',
                borderRadius: '4px',
                fontSize: '0.7rem',
                color: '#6c757d'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-around', gap: '8px' }}>
                  {match.team1_set1 !== null && (
                    <span>
                      {t('tournamentDetail.set')} 1: <strong>{match.team1_set1}:{match.team2_set1}</strong>
                    </span>
                  )}
                  {match.team1_set2 !== null && (
                    <span>
                      {t('tournamentDetail.set')} 2: <strong>{match.team1_set2}:{match.team2_set2}</strong>
                    </span>
                  )}
                  {match.team1_set3 !== null && (
                    <span>
                      {t('tournamentDetail.set')} 3: <strong>{match.team1_set3}:{match.team2_set3}</strong>
                    </span>
                  )}
                </div>
              </div>
            )}
            <div style={{
              marginTop: '6px',
              textAlign: 'center',
              color: '#22c55e',
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }}>
              {t('tournamentDetail.finished')}
            </div>
          </>
        )}
      </div>
    );
  };

  // Render Group Matches with Statistics (for Spielverlauf tab)
  const renderGroupsMatches = () => {
    const groups = {};
    const groupTeams = {};

    // Gruppiere Matches und Teams
    matches.forEach(match => {
      if (match.round && match.round.startsWith('Gruppe ')) {
        const groupName = match.round; // Keep full name "Gruppe A"
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(match);
      }
    });

    teams.forEach(team => {
      if (team.group_name) {
        if (!groupTeams[team.group_name]) {
          groupTeams[team.group_name] = [];
        }
        groupTeams[team.group_name].push(team);
      }
    });

    const sortedGroupNames = Object.keys(groups).sort();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', width: '100%' }}>
        {sortedGroupNames.map(groupName => {
          const groupMatches = groups[groupName].sort((a, b) => a.match_number - b.match_number);
          const teamsInGroup = groupTeams[groupName] || [];

          // Berechne Statistiken für jedes Team in der Gruppe
          const teamStats = teamsInGroup.map(team => {
            const teamMatches = groupMatches.filter(match =>
              match.team1_id === team.id || match.team2_id === team.id
            );

            let points = 0;
            let wins = 0;
            let losses = 0;
            let draws = 0;
            let setsWon = 0;
            let setsLost = 0;
            let gamesWon = 0;
            let gamesLost = 0;

            teamMatches.forEach(match => {
              // Check for Best-of-3 format
              const hasSetResult = match.team1_set1 !== null && match.team2_set1 !== null;
              // Check for simple score format
              const hasScoreResult = match.team1_score !== null && match.team2_score !== null;

              if (!hasSetResult && !hasScoreResult) return;

              const isTeam1 = match.team1_id === team.id;

              if (hasSetResult) {
                // Best-of-3 calculation
                // Count sets won by each team
                let team1Sets = 0;
                let team2Sets = 0;

                // Set 1
                if (match.team1_set1 !== null && match.team2_set1 !== null) {
                  if (match.team1_set1 > match.team2_set1) team1Sets++;
                  else if (match.team2_set1 > match.team1_set1) team2Sets++;

                  if (isTeam1) {
                    gamesWon += match.team1_set1 || 0;
                    gamesLost += match.team2_set1 || 0;
                  } else {
                    gamesWon += match.team2_set1 || 0;
                    gamesLost += match.team1_set1 || 0;
                  }
                }

                // Set 2
                if (match.team1_set2 !== null && match.team2_set2 !== null) {
                  if (match.team1_set2 > match.team2_set2) team1Sets++;
                  else if (match.team2_set2 > match.team1_set2) team2Sets++;

                  if (isTeam1) {
                    gamesWon += match.team1_set2 || 0;
                    gamesLost += match.team2_set2 || 0;
                  } else {
                    gamesWon += match.team2_set2 || 0;
                    gamesLost += match.team1_set2 || 0;
                  }
                }

                // Set 3 (if played)
                if (match.team1_set3 !== null && match.team2_set3 !== null) {
                  if (match.team1_set3 > match.team2_set3) team1Sets++;
                  else if (match.team2_set3 > match.team1_set3) team2Sets++;

                  if (isTeam1) {
                    gamesWon += match.team1_set3 || 0;
                    gamesLost += match.team2_set3 || 0;
                  } else {
                    gamesWon += match.team2_set3 || 0;
                    gamesLost += match.team1_set3 || 0;
                  }
                }

                // Determine winner and update stats
                if (isTeam1) {
                  setsWon += team1Sets;
                  setsLost += team2Sets;
                  if (team1Sets > team2Sets) {
                    points += 2;
                    wins += 1;
                  } else {
                    losses += 1;
                  }
                } else {
                  setsWon += team2Sets;
                  setsLost += team1Sets;
                  if (team2Sets > team1Sets) {
                    points += 2;
                    wins += 1;
                  } else {
                    losses += 1;
                  }
                }
              } else if (hasScoreResult) {
                // Simple score calculation
                if (isTeam1) {
                  gamesWon += match.team1_score || 0;
                  gamesLost += match.team2_score || 0;
                  if (match.team1_score > match.team2_score) {
                    points += 2;
                    wins += 1;
                  } else if (match.team1_score < match.team2_score) {
                    losses += 1;
                  } else {
                    points += 1; // Draw
                    draws += 1;
                  }
                } else {
                  gamesWon += match.team2_score || 0;
                  gamesLost += match.team1_score || 0;
                  if (match.team2_score > match.team1_score) {
                    points += 2;
                    wins += 1;
                  } else if (match.team2_score < match.team1_score) {
                    losses += 1;
                  } else {
                    points += 1; // Draw
                    draws += 1;
                  }
                }
              }
            });

            const setsDifference = setsWon - setsLost;
            const gamesDifference = gamesWon - gamesLost;

            return {
              team,
              points,
              wins,
              losses,
              draws,
              setsWon,
              setsLost,
              setsDifference,
              gamesWon,
              gamesLost,
              gamesDifference
            };
          }).sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points;
            if (b.setsDifference !== a.setsDifference) return b.setsDifference - a.setsDifference;
            if (b.gamesDifference !== a.gamesDifference) return b.gamesDifference - a.gamesDifference;
            return 0;
          });

          return (
            <div key={groupName} style={{
              padding: '20px',
              backgroundColor: 'rgba(245, 242, 239, 0.95)',
              border: '2px solid rgba(206, 212, 218, 0.6)',
              borderRadius: '8px',
              width: '100%'
            }}>
              <h4 style={{
                marginTop: '0',
                marginBottom: '20px',
                color: '#e8b73a',
                fontSize: '1.4rem',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                {groupName} - {t('tournamentDetail.standings')}
              </h4>

              {/* Gruppentabelle - Full Width Dynamic */}
              <div style={{ marginBottom: '20px', backgroundColor: 'white', padding: '16px', borderRadius: '6px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: tournament?.best_of_three
                    ? '2fr 90px 90px 90px 110px 110px'
                    : '2fr 70px 70px 70px 70px 90px',
                  gap: '20px',
                  padding: '14px 20px',
                  fontWeight: 'bold',
                  color: '#495057',
                  fontSize: '1.05rem',
                  borderBottom: '2px solid rgba(206, 212, 218, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  minWidth: '700px'
                }}>
                  <div>Team</div>
                  <div style={{ textAlign: 'center' }}>{t('tournamentDetail.pts')}</div>
                  <div style={{ textAlign: 'center' }}>{t('tournamentDetail.wins')}</div>
                  <div style={{ textAlign: 'center' }}>{t('tournamentDetail.losses')}</div>
                  {tournament?.best_of_three ? (
                    <>
                      <div style={{ textAlign: 'center' }}>{t('tournamentDetail.sets')}</div>
                      <div style={{ textAlign: 'center' }}>Games</div>
                    </>
                  ) : (
                    <>
                      <div style={{ textAlign: 'center' }}>{t('tournamentDetail.draws')}</div>
                      <div style={{ textAlign: 'center' }}>{t('tournamentDetail.diff')}</div>
                    </>
                  )}
                </div>

                {teamStats.map((stat, index) => (
                  <div key={stat.team.id} style={{
                    display: 'grid',
                    gridTemplateColumns: tournament?.best_of_three
                      ? '2fr 90px 90px 90px 110px 110px'
                      : '2fr 70px 70px 70px 70px 90px',
                    gap: '20px',
                    padding: '14px 20px',
                    borderBottom: index < teamStats.length - 1 ? '1px solid rgba(206, 212, 218, 0.3)' : 'none',
                    backgroundColor: index < 2 ? 'rgba(232, 183, 58, 0.1)' : 'transparent',
                    minWidth: '700px'
                  }}>
                    <div style={{
                      fontWeight: '600',
                      color: '#0F172A',
                      fontSize: '1rem'
                    }}>
                      {stat.team.players?.map(p => p.name).join(' / ')}
                    </div>
                    <div style={{ textAlign: 'center', color: '#0F172A', fontWeight: 'bold', fontSize: '1rem' }}>{stat.points}</div>
                    <div style={{ textAlign: 'center', color: '#22c55e', fontWeight: '600', fontSize: '0.95rem' }}>{stat.wins}</div>
                    <div style={{ textAlign: 'center', color: '#ef4444', fontWeight: '600', fontSize: '0.95rem' }}>{stat.losses}</div>
                    {tournament?.best_of_three ? (
                      <>
                        <div style={{
                          textAlign: 'center',
                          color: stat.setsDifference >= 0 ? '#22c55e' : '#ef4444',
                          fontWeight: 'bold',
                          fontSize: '0.95rem'
                        }}>
                          {stat.setsWon}:{stat.setsLost}
                        </div>
                        <div style={{
                          textAlign: 'center',
                          color: stat.gamesDifference >= 0 ? '#22c55e' : '#ef4444',
                          fontWeight: 'bold',
                          fontSize: '0.95rem'
                        }}>
                          {stat.gamesWon}:{stat.gamesLost}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ textAlign: 'center', color: '#6c757d', fontWeight: '600', fontSize: '0.95rem' }}>{stat.draws}</div>
                        <div style={{
                          textAlign: 'center',
                          color: stat.gamesDifference >= 0 ? '#22c55e' : '#ef4444',
                          fontWeight: 'bold',
                          fontSize: '0.95rem'
                        }}>
                          {stat.gamesDifference > 0 ? '+' : ''}{stat.gamesDifference}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Matches */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {groupMatches.map(match => renderMatchCard(match))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Groups Overview (Top 3 only) - for Tabelle tab
  const renderGroupsStandings = () => {
    // Calculate final standings from all teams
    const allTeamsWithStats = teams.map(team => {
      const teamMatches = matches.filter(match =>
        (match.team1_id === team.id || match.team2_id === team.id) &&
        match.team1_score !== null && match.team2_score !== null
      );

      let points = 0;
      let wins = 0;
      let losses = 0;
      let draws = 0;
      let goalsFor = 0;
      let goalsAgainst = 0;

      teamMatches.forEach(match => {
        const t1_score = match.team1_score || 0;
        const t2_score = match.team2_score || 0;

        if (match.team1_id === team.id) {
          goalsFor += t1_score;
          goalsAgainst += t2_score;
          if (t1_score > t2_score) {
            points += 3;
            wins += 1;
          } else if (t2_score > t1_score) {
            losses += 1;
          } else {
            points += 1;
            draws += 1;
          }
        } else if (match.team2_id === team.id) {
          goalsFor += t2_score;
          goalsAgainst += t1_score;
          if (t2_score > t1_score) {
            points += 3;
            wins += 1;
          } else if (t1_score > t2_score) {
            losses += 1;
          } else {
            points += 1;
            draws += 1;
          }
        }
      });

      const goalDifference = goalsFor - goalsAgainst;

      return {
        team,
        points,
        wins,
        losses,
        draws,
        goalDifference,
        goalsFor,
        goalsAgainst
      };
    });

    // Sort all teams (alle Platzierungen, nicht nur Top 3)
    const allTeamsSorted = allTeamsWithStats
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
        return b.goalsFor - a.goalsFor;
      });

    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="card" style={{
          padding: '12px',
          backgroundColor: 'rgba(245, 242, 239, 0.9)',
          border: '1px solid rgba(206, 212, 218, 0.5)'
        }}>
          <h3 style={{ color: '#e8b73a', marginTop: '0', marginBottom: '15px', fontSize: '1.1rem' }}>{t('tournamentDetail.standings')}</h3>

          {/* Tabelle mit allen Platzierungen */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8b73a' }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#e8b73a' }}>{t('tournamentDetail.rank')}</th>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#e8b73a' }}>{t('tournamentDetail.player')}</th>
                </tr>
              </thead>
              <tbody>
                {allTeamsSorted.map((teamData, index) => {
                  const medals = ['🥇', '🥈', '🥉'];
                  return (
                    <tr key={teamData.team.id} style={{
                      borderBottom: '1px solid rgba(206, 212, 218, 0.3)',
                      backgroundColor: index < 3 ? 'rgba(232, 183, 58, 0.1)' : 'transparent'
                    }}>
                      <td style={{
                        padding: '10px 8px',
                        fontWeight: index < 3 ? 'bold' : 'normal',
                        fontSize: index < 3 ? '1.1rem' : '0.9rem',
                        color: '#0F172A'
                      }}>
                        {index < 3 ? medals[index] + ' ' : ''}{index + 1}.
                      </td>
                      <td style={{
                        padding: '10px 8px',
                        fontWeight: index < 3 ? 'bold' : 'normal',
                        color: '#0F172A'
                      }}>
                        {teamData.team.players?.map(p => p.name).join(' / ') || teamData.team.name}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // OLD VERSION - keeping for reference of group tables
  // eslint-disable-next-line no-unused-vars
  const renderGroupsStandingsOld = () => {
    const groups = {};
    teams.forEach(team => {
      if (team.group_name) {
        if (!groups[team.group_name]) {
          groups[team.group_name] = [];
        }
        groups[team.group_name].push(team);
      }
    });

    const sortedGroupNames = Object.keys(groups).sort();

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
        {sortedGroupNames.map(groupName => {
          const groupTeams = groups[groupName];
          return (
            <div key={groupName} className="card" style={{
              padding: '16px',
              backgroundColor: 'rgba(245, 242, 239, 0.9)',
              border: '1px solid rgba(206, 212, 218, 0.5)'
            }}>
              <h4 style={{
                marginTop: '0',
                marginBottom: '16px',
                color: '#e8b73a',
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}>
                Gruppe {groupName}
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 60px 60px 60px 60px 60px',
                  gap: '16px',
                  padding: '8px 0',
                  fontWeight: 'bold',
                  color: '#6c757d',
                  fontSize: '0.9rem',
                  borderBottom: '1px solid rgba(206, 212, 218, 0.5)'
                }}>
                  <div>Team</div>
                  <div style={{ textAlign: 'center' }}>Pkt.</div>
                  <div style={{ textAlign: 'center' }}>Win</div>
                  <div style={{ textAlign: 'center' }}>Loss</div>
                  <div style={{ textAlign: 'center' }}>Draw</div>
                  <div style={{ textAlign: 'center' }}>Dif.</div>
                </div>

                {groupTeams.map(team => {
                  const teamMatches = matches.filter(match =>
                    (match.team1_id === team.id || match.team2_id === team.id) &&
                    match.round === `Gruppe ${groupName}` &&
                    match.team1_score !== null && match.team2_score !== null
                  );

                  let points = 0;
                  let wins = 0;
                  let losses = 0;
                  let draws = 0;
                  let goalsFor = 0;
                  let goalsAgainst = 0;

                  teamMatches.forEach(match => {
                    const t1_score = match.team1_score || 0;
                    const t2_score = match.team2_score || 0;

                    if (match.team1_id === team.id) {
                      goalsFor += t1_score;
                      goalsAgainst += t2_score;
                      if (t1_score > t2_score) {
                        points += 3;
                        wins += 1;
                      } else if (t2_score > t1_score) {
                        losses += 1;
                      } else {
                        points += 1;
                        draws += 1;
                      }
                    } else if (match.team2_id === team.id) {
                      goalsFor += t2_score;
                      goalsAgainst += t1_score;
                      if (t2_score > t1_score) {
                        points += 3;
                        wins += 1;
                      } else if (t1_score > t2_score) {
                        losses += 1;
                      } else {
                        points += 1;
                        draws += 1;
                      }
                    }
                  });

                  const goalDifference = goalsFor - goalsAgainst;

                  return {
                    team,
                    points,
                    wins,
                    losses,
                    draws,
                    goalDifference,
                    goalsFor,
                    goalsAgainst
                  };
                })
                .sort((a, b) => {
                  if (b.points !== a.points) return b.points - a.points;
                  if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
                  return b.goalsFor - a.goalsFor;
                })
                .map((teamData) => (
                  <div key={teamData.team.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 60px 60px 60px 60px 60px',
                    gap: '16px',
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(206, 212, 218, 0.5)'
                  }}>
                    <div style={{ fontWeight: '600', color: '#0F172A' }}>
                      {teamData.team.players?.map(p => p.name).join(' / ')}
                    </div>
                    <div style={{ textAlign: 'center', color: '#495057' }}>{teamData.points}</div>
                    <div style={{ textAlign: 'center', color: '#495057' }}>{teamData.wins}</div>
                    <div style={{ textAlign: 'center', color: '#495057' }}>{teamData.losses}</div>
                    <div style={{ textAlign: 'center', color: '#495057' }}>{teamData.draws}</div>
                    <div style={{ textAlign: 'center', color: '#495057' }}>
                      {teamData.goalDifference >= 0 ? `+${teamData.goalDifference}` : teamData.goalDifference}
                    </div>
                  </div>
                ))}
              </div>

              {/* Group matches */}
              <div style={{ marginTop: '20px' }}>
                <h5 style={{ color: '#6c757d', fontSize: '1rem', marginBottom: '12px' }}>Gruppenspiele</h5>
                {matches
                  .filter(match => match.round === `Gruppe ${groupName}`)
                  .sort((a, b) => a.match_number - b.match_number)
                  .map(match => renderMatchCard(match))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render KO matches - Mobile Optimized
  const renderKOMatches = () => {
    const rounds = {
      'Viertelfinale': [],
      'Halbfinale': [],
      'Finale': [],
      'Spiel um Platz 3': []
    };

    const sortedMatches = [...matches].sort((a, b) => {
      const roundOrder = {
        'Viertelfinale': 1,
        'Halbfinale': 2,
        'Spiel um Platz 3': 3,
        'Finale': 4
      };

      const aRoundOrder = roundOrder[a.round] || 99;
      const bRoundOrder = roundOrder[b.round] || 99;

      if (aRoundOrder !== bRoundOrder) {
        return aRoundOrder - bRoundOrder;
      }

      return (a.match_number || 0) - (b.match_number || 0);
    });

    sortedMatches.forEach(match => {
      if (rounds[match.round]) {
        rounds[match.round].push(match);
      }
    });

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '15px',
        marginTop: '20px'
      }}>
        {Object.entries(rounds).map(([roundName, roundMatches]) => {
          if (roundMatches.length === 0) return null;

          return (
            <div key={roundName} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              background: '#f8f8f8',
              border: '1px solid rgba(206, 212, 218, 0.3)',
              borderRadius: '8px',
              padding: '12px'
            }}>
              <h3 style={{ color: '#e8b73a', marginBottom: '8px', textAlign: 'center', fontSize: '1.1rem' }}>{translateRound(roundName)}</h3>
              {roundMatches.map((match, index) => (
                <div key={match.id}>
                  {renderMatchCard(match)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  // Render Placement Matches
  const renderPlacementMatches = () => {
    const placementMatches = matches.filter(m =>
      m.placement_category &&
      m.placement_category !== 'main' &&
      !m.round.startsWith('Gruppe ')
    );

    if (placementMatches.length === 0) {
      return null;
    }

    // Dynamisch alle Kategorien aus den Matches ermitteln
    const categories = {};
    placementMatches.forEach(match => {
      const cat = match.placement_category;
      if (!categories[cat]) {
        // Extrahiere Platznummern aus "placement_X_Y"
        const parts = cat.replace('placement_', '').split('_');
        const startPlace = parts[0] ? parseInt(parts[0]) : 0;
        const endPlace = parts[1] ? parseInt(parts[1]) : startPlace;
        categories[cat] = {
          title: `${t('tournamentDetail.placement')} ${startPlace}-${endPlace}`,
          matches: [],
          startPlace
        };
      }
      categories[cat].matches.push(match);
    });

    // Sortiere Kategorien nach Startnummer
    const sortedCategories = Object.entries(categories).sort((a, b) =>
      a[1].startPlace - b[1].startPlace
    );

    return (
      <div style={{ marginTop: '40px' }}>
        <h2 style={{ color: '#ff8c00', marginBottom: '20px' }}>{t('tournamentDetail.placementMatches')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
          {sortedCategories.map(([key, category]) => {
            if (category.matches.length === 0) return null;

            // Gruppiere Matches nach Runden innerhalb der Kategorie
            const roundsMap = {};
            category.matches.forEach(match => {
              if (!roundsMap[match.round]) {
                roundsMap[match.round] = [];
              }
              roundsMap[match.round].push(match);
            });

            // Sortiere Runden: Halbfinale vor Finals
            const sortedRounds = Object.keys(roundsMap).sort((a, b) => {
              if (a.includes('HF') && !b.includes('HF')) return -1;
              if (!a.includes('HF') && b.includes('HF')) return 1;
              return 0;
            });

            return (
              <div key={key} style={{
                border: '2px solid #ff8c00',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: 'rgba(255, 140, 0, 0.05)'
              }}>
                <h3 style={{ color: '#ff8c00', marginTop: '0', marginBottom: '15px', textAlign: 'center', fontSize: '1.1rem' }}>
                  {category.title}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {sortedRounds.map(roundName => (
                    <div key={roundName}>
                      <h4 style={{
                        color: '#666',
                        fontSize: '0.95rem',
                        marginBottom: '8px',
                        fontWeight: '600',
                        textAlign: 'center'
                      }}>
                        {roundName}
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {roundsMap[roundName]
                          .sort((a, b) => a.match_number - b.match_number)
                          .map(match => (
                            <div key={match.id}>
                              {renderMatchCard(match)}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render KO mit Platzierungen view
  const renderKOPlacementView = () => {
    const matchesByRound = {};
    matches.forEach(m => {
      if (!matchesByRound[m.round]) matchesByRound[m.round] = [];
      matchesByRound[m.round].push(m);
    });
    Object.keys(matchesByRound).forEach(r => {
      matchesByRound[r].sort((a, b) => (a.match_number || 0) - (b.match_number || 0));
    });

    const sections = [
      {
        title: t('tournamentDetail.mainBracket'),
        color: '#e8b73a',
        rounds: ['Runde 1', 'Viertelfinale', 'Halbfinale', 'Finale', 'Spiel um Platz 3'],
      },
      {
        title: t('tournamentDetail.places5to8'),
        color: '#fb923c',
        rounds: ['Platz 5-8 Halbfinale', 'Spiel um Platz 5', 'Spiel um Platz 7'],
      },
      {
        title: t('tournamentDetail.places9to16'),
        color: '#60a5fa',
        rounds: ['Platz 9-16 Viertelfinale', 'Platz 9-12 Halbfinale', 'Platz 13-16 Halbfinale',
                 'Spiel um Platz 9', 'Spiel um Platz 11', 'Spiel um Platz 13', 'Spiel um Platz 15'],
      },
    ];

    return (
      <div>
        {sections.map(section => {
          const sectionRounds = section.rounds.filter(r => matchesByRound[r]?.length > 0);
          if (sectionRounds.length === 0) return null;

          return (
            <div key={section.title} style={{ marginBottom: '40px' }}>
              <h2 style={{ color: section.color, marginBottom: '16px', fontSize: '1.3rem' }}>
                {section.title}
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))`,
                gap: '15px',
              }}>
                {sectionRounds.map(roundName => (
                  <div key={roundName} style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${section.color}44`,
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <h3 style={{
                      color: section.color, textAlign: 'center',
                      marginBottom: '10px', fontSize: '1rem', opacity: 0.9
                    }}>
                      {roundName}
                    </h3>
                    {matchesByRound[roundName].map(match => (
                      <div key={match.id} style={{ marginBottom: '10px' }}>
                        {renderMatchCard(match)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Group Matches
  // eslint-disable-next-line no-unused-vars
  const renderGroupMatches = () => {
    const groups = {};
    matches.forEach(match => {
      if (match.round && match.round.startsWith('Gruppe ')) {
        const groupName = match.round;
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(match);
      }
    });

    return (
      <div>
        {Object.entries(groups).map(([groupName, groupMatches]) => (
          <div key={groupName} style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#e8b73a', marginBottom: '20px', fontSize: '1.5rem' }}>{groupName}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {groupMatches.map(match => renderMatchCard(match))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Mexicano Matches - Mobile Optimized
  const renderMexicanoMatches = () => {
    const rounds = {};
    matches.forEach(match => {
      if (!rounds[match.round]) rounds[match.round] = [];
      rounds[match.round].push(match);
    });

    // Sortiere Runden: Extrahiere die Nummer aus "Runde X" und sortiere aufsteigend
    const sortedRounds = Object.keys(rounds).sort((a, b) => {
      const numA = parseInt(a.replace('Runde ', '')) || 0;
      const numB = parseInt(b.replace('Runde ', '')) || 0;
      return numA - numB;
    });

    return (
      <div>
        {sortedRounds.map(round => (
          <div key={round} style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#e8b73a', marginBottom: '15px', fontSize: '1.2rem' }}>{t('common.round')} {parseInt(round.replace('Runde ', '')) || round}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {rounds[round]
                .sort((a, b) => a.match_number - b.match_number)
                .map(match => renderMatchCard(match))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Mexicano Standings (for Tabelle tab)
  const renderMexicanoStandings = () => {
    // Calculate detailed statistics for each team
    const teamsWithStats = teams.map(team => {
      const teamMatches = matches.filter(match =>
        (match.team1_id === team.id || match.team2_id === team.id ||
         match.team3_id === team.id || match.team4_id === team.id) &&
        match.team1_score !== null && match.team2_score !== null
      );

      let wins = 0;
      let losses = 0;
      let draws = 0;
      let goalsFor = 0;
      let goalsAgainst = 0;

      teamMatches.forEach(match => {
        const t1_score = match.team1_score || 0;
        const t2_score = match.team2_score || 0;

        // Team1 or Team3 (same side)
        if (match.team1_id === team.id || match.team3_id === team.id) {
          goalsFor += t1_score;
          goalsAgainst += t2_score;
          if (t1_score > t2_score) {
            wins += 1;
          } else if (t2_score > t1_score) {
            losses += 1;
          } else {
            draws += 1;
          }
        }
        // Team2 or Team4 (same side)
        else if (match.team2_id === team.id || match.team4_id === team.id) {
          goalsFor += t2_score;
          goalsAgainst += t1_score;
          if (t2_score > t1_score) {
            wins += 1;
          } else if (t1_score > t2_score) {
            losses += 1;
          } else {
            draws += 1;
          }
        }
      });

      const goalDifference = goalsFor - goalsAgainst;

      return {
        ...team,
        wins,
        losses,
        draws,
        goalDifference
      };
    });

    // Sort teams by mexicano_points
    const sortedTeams = teamsWithStats.sort((a, b) => (b.mexicano_points || 0) - (a.mexicano_points || 0));

    return (
      <div style={{ maxWidth: '100%', margin: '0 auto', overflowX: 'auto' }}>
        <div className="card" style={{
          padding: '12px',
          backgroundColor: 'rgba(245, 242, 239, 0.9)',
          border: '1px solid rgba(206, 212, 218, 0.5)'
        }}>
          <h3 style={{ color: '#e8b73a', marginTop: '0', marginBottom: '15px', fontSize: '1.1rem' }}>{t('tournamentDetail.liveStandings')}</h3>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 50px 40px 40px 40px 50px', gap: '6px', marginBottom: '10px', minWidth: '500px' }}>
              <div style={{ fontWeight: 'bold', color: '#6c757d', textAlign: 'center', fontSize: '0.75rem' }}>{t('tournamentDetail.rank')}</div>
              <div style={{ fontWeight: 'bold', color: '#6c757d', fontSize: '0.75rem' }}>{t('tournamentDetail.player')}</div>
              <div style={{ fontWeight: 'bold', color: '#6c757d', textAlign: 'center', fontSize: '0.75rem' }}>{t('tournamentDetail.pts')}</div>
              <div style={{ fontWeight: 'bold', color: '#6c757d', textAlign: 'center', fontSize: '0.75rem' }}>{t('tournamentDetail.wins')}</div>
              <div style={{ fontWeight: 'bold', color: '#6c757d', textAlign: 'center', fontSize: '0.75rem' }}>{t('tournamentDetail.losses')}</div>
              <div style={{ fontWeight: 'bold', color: '#6c757d', textAlign: 'center', fontSize: '0.75rem' }}>{t('tournamentDetail.draws')}</div>
              <div style={{ fontWeight: 'bold', color: '#6c757d', textAlign: 'center', fontSize: '0.75rem' }}>{t('tournamentDetail.diff')}</div>
            </div>

            {sortedTeams.map((team, index) => (
              <div key={team.id} style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 50px 40px 40px 40px 50px',
                gap: '6px',
                padding: '8px 0',
                borderTop: '1px solid rgba(206, 212, 218, 0.3)',
                alignItems: 'center',
                minWidth: '500px'
              }}>
                <div style={{
                  textAlign: 'center',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: index === 0 ? '#e8b73a' : '#0F172A'
                }}>
                  {index + 1}
                </div>
                <div style={{ minWidth: '0' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '2px', color: '#0F172A', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(tournament.tournament_type === 'mexicano' || tournament.tournament_type === 'americano')
                      ? (team.players?.[0]?.name || team.name)
                      : team.name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {team.players?.map(p => p.name).join(' / ')}
                  </div>
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: '#e8b73a'
                }}>
                  {team.mexicano_points || 0}
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  color: '#0F172A'
                }}>
                  {team.wins}
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  color: '#0F172A'
                }}>
                  {team.losses}
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  color: '#0F172A'
                }}>
                  {team.draws}
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  color: team.goalDifference >= 0 ? '#22c55e' : '#ef4444'
                }}>
                  {team.goalDifference >= 0 ? `+${team.goalDifference}` : team.goalDifference}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render KO Placement Standings (for Tabelle tab)
  const renderKOPlacementStandings = () => {
    const placementMatchDefs = [
      { round: 'Finale',              winnerPlace: 1,  loserPlace: 2  },
      { round: 'Spiel um Platz 3',    winnerPlace: 3,  loserPlace: 4  },
      { round: 'Spiel um Platz 5',    winnerPlace: 5,  loserPlace: 6  },
      { round: 'Spiel um Platz 7',    winnerPlace: 7,  loserPlace: 8  },
      { round: 'Spiel um Platz 9',    winnerPlace: 9,  loserPlace: 10 },
      { round: 'Spiel um Platz 11',   winnerPlace: 11, loserPlace: 12 },
      { round: 'Spiel um Platz 13',   winnerPlace: 13, loserPlace: 14 },
      { round: 'Spiel um Platz 15',   winnerPlace: 15, loserPlace: 16 },
    ];

    const getWinnerId = (match) => {
      if (match.status !== 'beendet') return null;
      if (match.team1_set1 !== null && match.team2_set1 !== null) {
        let s1 = 0, s2 = 0;
        if (match.team1_set1 > match.team2_set1) s1++; else if (match.team2_set1 > match.team1_set1) s2++;
        if (match.team1_set2 !== null && match.team2_set2 !== null) {
          if (match.team1_set2 > match.team2_set2) s1++; else if (match.team2_set2 > match.team1_set2) s2++;
        }
        return s1 >= s2 ? match.team1_id : match.team2_id;
      }
      if (match.team1_score !== null && match.team2_score !== null) {
        return match.team1_score >= match.team2_score ? match.team1_id : match.team2_id;
      }
      return match.team1_id;
    };

    const rows = [];
    const placedTeamIds = new Set();

    placementMatchDefs.forEach(({ round, winnerPlace, loserPlace }) => {
      const match = matches.find(m => m.round === round);
      if (!match) return;

      const team1 = teams.find(t => t.id === match.team1_id);
      const team2 = teams.find(t => t.id === match.team2_id);

      if (match.status === 'beendet' && team1 && team2) {
        const winnerId = getWinnerId(match);
        const winner = winnerId === team1.id ? team1 : team2;
        const loser = winnerId === team1.id ? team2 : team1;
        rows.push({ place: winnerPlace, team: winner, status: 'done' });
        rows.push({ place: loserPlace, team: loser, status: 'done' });
        placedTeamIds.add(winner.id);
        placedTeamIds.add(loser.id);
      } else if (team1 && team2) {
        rows.push({ place: winnerPlace, team: team1, team2, status: 'playing', label: `Platz ${winnerPlace} oder ${loserPlace}` });
        rows.push({ place: loserPlace, team: team2, team2: team1, status: 'playing', label: `Platz ${winnerPlace} oder ${loserPlace}` });
        placedTeamIds.add(team1.id);
        placedTeamIds.add(team2.id);
      }
    });

    // Teams still in earlier rounds
    const activeTeams = teams.filter(t => !placedTeamIds.has(t.id));
    activeTeams.forEach(team => {
      rows.push({ place: null, team, status: 'active' });
    });

    rows.sort((a, b) => {
      if (a.place === null && b.place === null) return 0;
      if (a.place === null) return 1;
      if (b.place === null) return -1;
      return a.place - b.place;
    });

    const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };

    return (
      <div className="card" style={{ padding: '12px', backgroundColor: 'rgba(245,242,239,0.9)', border: '1px solid rgba(206,212,218,0.5)' }}>
        <h3 style={{ color: '#e8b73a', marginTop: 0, marginBottom: '15px', fontSize: '1.1rem' }}>{t('tournamentDetail.currentPlacements')}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {rows.map((row, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '8px 10px', borderRadius: '6px',
              backgroundColor: row.place <= 3 ? 'rgba(232,183,58,0.1)' : 'transparent',
              borderBottom: '1px solid rgba(206,212,218,0.2)',
              opacity: row.status === 'active' ? 0.7 : 1,
            }}>
              <div style={{ minWidth: '36px', fontWeight: 'bold', fontSize: '1rem', color: '#e8b73a' }}>
                {row.place ? (medals[row.place] ? medals[row.place] : `${row.place}.`) : '–'}
              </div>
              <div style={{ flex: 1, fontWeight: row.place <= 3 ? 'bold' : 'normal', color: '#0F172A' }}>
                {getDisplayName(row.team)}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>
                {row.status === 'done' && t('tournamentDetail.done')}
                {row.status === 'playing' && '⏳ ' + row.label}
                {row.status === 'active' && t('tournamentDetail.inTournament')}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Teams List - Mobile Optimized
  const renderTeamsList = () => {
    // Bei Mexicano/Americano: Zeige Spielerliste statt Teamliste
    if (tournament.tournament_type === 'mexicano' || tournament.tournament_type === 'americano') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
          {teams.map(team => (
            <div key={team.id} className="card" style={{
              padding: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(206, 212, 218, 0.5)'
            }}>
              <h4 style={{
                marginTop: '0',
                marginBottom: '0',
                color: '#e8b73a',
                fontSize: '0.95rem',
                fontWeight: 'bold'
              }}>
                {team.players?.[0]?.name || team.name}
              </h4>
            </div>
          ))}
        </div>
      );
    }

    // Standard: Zeige Teams mit Spielern
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
        {teams.map(team => (
          <div key={team.id} className="card" style={{
            padding: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(206, 212, 218, 0.5)'
          }}>
            <h4 style={{
              marginTop: '0',
              marginBottom: '8px',
              color: '#e8b73a',
              fontSize: '0.95rem',
              fontWeight: 'bold'
            }}>
              {team.name}
            </h4>
            <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
              {team.players && team.players.map((player, idx) => (
                <div key={player.id} style={{ marginBottom: '3px' }}>
                  {player.name}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card">
        <button onClick={() => navigate('/')} className="back-button">
          {t('tournamentDetail.backToOverview')}
        </button>
        <h2>{t('tournamentDetail.details')}</h2>
        <p>{t('tournamentDetail.loading')}</p>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="card">
        <button onClick={() => navigate('/')} className="back-button">
          {t('tournamentDetail.backToOverview')}
        </button>
        <h2>{t('tournamentDetail.details')}</h2>
        <div className="card" style={{
          padding: '20px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          marginTop: '20px'
        }}>
          <p style={{ margin: 0, color: '#fca5a5' }}>
            {error || t('tournamentDetail.notFound')}
          </p>
        </div>
      </div>
    );
  }

  // Render content based on active tab
  const renderTabContent = () => {
    if (activeTab === 'spielverlauf') {
      // Spielverlauf: Show all matches
      if (tournament.tournament_type === 'groups') {
        return (
          <>
            {renderGroupsMatches()}
            {matches.some(m => !m.round.startsWith('Gruppe ')) && (
              <>
                <h2 style={{ color: '#e8b73a', marginTop: '40px', marginBottom: '20px' }}>{t('tournamentDetail.knockoutPhase')}</h2>
                {renderKOMatches()}
                {renderPlacementMatches()}
              </>
            )}
          </>
        );
      } else if (tournament.tournament_type === 'ko') {
        return renderKOMatches();
      } else if (tournament.tournament_type === 'ko_placement') {
        return renderKOPlacementView();
      } else if (tournament.tournament_type === 'mexicano' || tournament.tournament_type === 'americano') {
        return renderMexicanoMatches();
      }
    } else if (activeTab === 'tabelle') {
      // Tabelle: Show standings
      if (tournament.tournament_type === 'mexicano' || tournament.tournament_type === 'americano') {
        return renderMexicanoStandings();
      } else if (tournament.tournament_type === 'ko_placement') {
        return renderKOPlacementStandings();
      } else if (tournament.tournament_type === 'groups') {
        // Check if tournament has final rankings (from backend)
        if (tournament.final_rankings && tournament.is_completed === 1) {
          // Show final rankings from backend
          const rankings = tournament.final_rankings.main || [];
          return (
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <div className="card" style={{
                padding: '12px',
                backgroundColor: 'rgba(245, 242, 239, 0.9)',
                border: '1px solid rgba(206, 212, 218, 0.5)'
              }}>
                <h3 style={{ color: '#e8b73a', marginTop: '0', marginBottom: '15px', fontSize: '1.1rem' }}>
                  {t('tournamentDetail.finalRankings')}
                </h3>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e8b73a' }}>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#e8b73a' }}>{t('tournamentDetail.rank')}</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#e8b73a' }}>{t('tournamentDetail.player')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankings.map((ranking, index) => {
                        const medals = ['🥇', '🥈', '🥉'];
                        const place = ranking.place || ranking.final_place || (index + 1);
                        return (
                          <tr key={index} style={{
                            borderBottom: '1px solid rgba(206, 212, 218, 0.3)',
                            backgroundColor: index < 3 ? 'rgba(232, 183, 58, 0.1)' : 'transparent'
                          }}>
                            <td style={{
                              padding: '10px 8px',
                              fontWeight: index < 3 ? 'bold' : 'normal',
                              fontSize: index < 3 ? '1.1rem' : '0.9rem',
                              color: '#0F172A'
                            }}>
                              {index < 3 ? medals[index] + ' ' : ''}{place}.
                            </td>
                            <td style={{
                              padding: '10px 8px',
                              fontWeight: index < 3 ? 'bold' : 'normal',
                              color: '#0F172A'
                            }}>
                              {ranking.team || t('tournamentDelete.unknown')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        } else {
          // Show current group standings
          return renderGroupsStandings();
        }
      }
    } else if (activeTab === 'teams') {
      return renderTeamsList();
    }
    return null;
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/')} className="back-button">
          {t('tournamentDetail.backToOverview')}
        </button>
        {(tournament.tournament_type === 'groups' || tournament.tournament_type === 'ko_placement') && tournament.start_time && (
          <button
            onClick={() => navigate(`/tournament/${tournament.id}/schedule`)}
            className="back-button"
            style={{
              background: '#e8b73a',
              color: 'white',
              border: 'none',
              marginLeft: 'auto'
            }}
          >
            {t('tournamentDetail.schedule')}
          </button>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <h2 style={{ margin: 0 }}>{tournament.name}</h2>
          {/* WebSocket Connection Status */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: wsConnected ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
            border: `1px solid ${wsConnected ? '#4CAF50' : '#f44336'}`,
            fontSize: '12px',
            fontWeight: '600',
            color: wsConnected ? '#4CAF50' : '#f44336'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: wsConnected ? '#4CAF50' : '#f44336'
            }}></div>
            {wsConnected ? 'Live' : 'Offline'}
          </div>
        </div>
        <div className="tournament-info" style={{ marginTop: '12px' }}>
          <div className="tournament-info-item">
            <span className="info-icon">📅</span>
            <span className="info-text">{formatGermanDate(tournament.date)}</span>
          </div>
          <div className="tournament-info-item">
            <span className="info-icon">👥</span>
            <span className="info-text">{tournament.num_teams} Teams</span>
          </div>
          <div className="tournament-info-item">
            <span className="info-icon">🏟️</span>
            <span className="info-text">{tournament.num_courts} Courts</span>
          </div>
          <div className="tournament-info-item">
            <span className="info-icon">🏆</span>
            <span className="info-text">
              {t(`type.${tournament.tournament_type}`) || tournament.tournament_type}
            </span>
          </div>
        </div>
        {tournament.description && (
          <p style={{ marginTop: '12px', fontSize: '0.95rem', opacity: 0.9 }}>
            {tournament.description}
          </p>
        )}
      </div>

      {/* Tab Navigation - Mobile Optimized */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '20px',
        borderBottom: '2px solid rgba(206, 212, 218, 0.3)',
        paddingBottom: '0',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        <button
          onClick={() => setActiveTab('spielverlauf')}
          style={{
            padding: '6px 8px',
            backgroundColor: activeTab === 'spielverlauf' ? '#e8b73a' : 'transparent',
            color: activeTab === 'spielverlauf' ? '#0F172A' : '#6c757d',
            border: 'none',
            borderBottom: activeTab === 'spielverlauf' ? '2px solid #e8b73a' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: activeTab === 'spielverlauf' ? 'bold' : 'normal',
            transition: 'all 0.2s',
            marginBottom: '-2px',
            whiteSpace: 'nowrap',
            flex: '0 0 auto',
            borderRadius: '4px 4px 0 0'
          }}
        >
          {t('tournamentDetail.tab.matches')}
        </button>
        <button
          onClick={() => setActiveTab('tabelle')}
          style={{
            padding: '6px 8px',
            backgroundColor: activeTab === 'tabelle' ? '#e8b73a' : 'transparent',
            color: activeTab === 'tabelle' ? '#0F172A' : '#6c757d',
            border: 'none',
            borderBottom: activeTab === 'tabelle' ? '2px solid #e8b73a' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: activeTab === 'tabelle' ? 'bold' : 'normal',
            transition: 'all 0.2s',
            marginBottom: '-2px',
            whiteSpace: 'nowrap',
            flex: '0 0 auto',
            borderRadius: '4px 4px 0 0'
          }}
        >
          {t('tournamentDetail.tab.standings')}
        </button>
        <button
          onClick={() => setActiveTab('teams')}
          style={{
            padding: '6px 8px',
            backgroundColor: activeTab === 'teams' ? '#e8b73a' : 'transparent',
            color: activeTab === 'teams' ? '#0F172A' : '#6c757d',
            border: 'none',
            borderBottom: activeTab === 'teams' ? '2px solid #e8b73a' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: activeTab === 'teams' ? 'bold' : 'normal',
            transition: 'all 0.2s',
            marginBottom: '-2px',
            whiteSpace: 'nowrap',
            flex: '0 0 auto',
            borderRadius: '4px 4px 0 0'
          }}
        >
          {t('tournamentDetail.tab.teams')}
        </button>
      </div>

      {/* Tab Content */}
      <div style={{ marginTop: '20px' }}>
        {renderTabContent()}
      </div>
    </div>
  );
}

export default TournamentDetail;
